import { useSyncReportUrl } from '@src/packages/dashboard/hooks/useSyncReportUrl';
import { useReportUrlFilters } from '@src/packages/dashboard/hooks/useReportUrlFilters';
/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { restoreClearedReportFilters } from '@src/packages/dashboard/utils/restoreClearedReportFilters';

import type { SetValueProps } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { useDivisionNames } from '@src/hooks/useDivisionNames';
import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import { SCHEDULE_TYPES } from '@src/constants/commons';
import { auth } from '@src/firebase/firebase';
import {
  getRouteToDashboardTasks,
  getRouteToTasksEventTypesDrawer,
  getRouteToTasksTimelineDrawer,
} from '@src/packages/dashboard/constants';
import { camelize } from '@src/packages/dashboard/utils';
import { getFullUrl, useElasticDivisionIds } from '@src/packages/utils';
import { DateUtil, disableCalendarDatesAfterToday } from '@src/packages/utils/dates';
import useGetParams from '@src/packages/utils/hooks/useGetParams';
import { get, set } from '@src/packages/utils/sessionStorage';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import { calculateScheduleProgress } from '../../SchedulesDashboard/constants';
import {
  getDashboardScheduleDivisionIds,
  getDashboardSchedulesById,
  getDashboardSchedulesWithDivisionNames,
} from '../../utils/getDashboardSchedulesById';
import {
  areTemplateSelectionsEqual,
  buildDefaultTaskChartFilters,
  buildInitTaskFilterValues,
  buildTaskFilterSetValueProps,
  filterValuesToTaskFilters,
  filterValuesToTaskFiltersUi,
  getTemplateIdsForApi,
  mergeFilterSetValueProps,
  normalizeStoredTaskFilters,
  TASKS_DASHBOARD_FILTER_FIELDS,
  TASKS_DASHBOARD_FILTERS_STORAGE_KEY,
  taskFiltersToStoragePayload,
  type TasksChartFilters,
} from '../tasksFilterConfig';
import type {
  GoToTimelineParams,
  OnOpenEventTypesDrawerParams,
  ScheduleDashboardDrawerState,
  ScheduleDashboardDrawerType,
  ScheduleDashboardSchedule,
  SelectedSchedulesState,
  SelectedUsersState,
  TableDataByDay,
  TasksDashboardContextValue,
  TasksDashboardReduxState,
  TimeAggregationBucket,
} from '../types';
import { getTasksReportCalendarRange, getTasksReportEpochRange } from '../utils/reportPeriod';

const EMPTY_ASSIGNED_DIVISIONS: CommonReduxState['divisions']['assigned']['data']['divisions'] = {};

export const TasksDashboardContext = createContext<TasksDashboardContextValue | null>(null);

interface TasksDashboardProviderProps {
  children: ReactNode;
}

export const TasksDashboardProvider = ({ children }: TasksDashboardProviderProps) => {
  const intl = useIntl();
  const { values: filters, setValue: setFilters, toggle, isOpen } = useSecondaryPanel();

  const urlFilters = useReportUrlFilters('templates');

  const initialChartFilters = useMemo(
    () =>
      normalizeStoredTaskFilters(urlFilters) ??
      normalizeStoredTaskFilters(get(TASKS_DASHBOARD_FILTERS_STORAGE_KEY)) ??
      buildDefaultTaskChartFilters(),
    [],
  );

  useEffect(() => {
    if (urlFilters) set(TASKS_DASHBOARD_FILTERS_STORAGE_KEY, taskFiltersToStoragePayload(initialChartFilters));
  }, [initialChartFilters, urlFilters]);

  const initialFiltersRef = useRef(buildInitTaskFilterValues(initialChartFilters, intl));

  const draftChartFilters = useMemo(
    () => filterValuesToTaskFiltersUi(filters, initialChartFilters),
    [filters, initialChartFilters],
  );
  const [chartFilters, setChartFilters] = useState(initialChartFilters);
  const hasPendingFilters =
    JSON.stringify(taskFiltersToStoragePayload(draftChartFilters)) !==
    JSON.stringify(taskFiltersToStoragePayload(chartFilters));

  const { relative, dateRange, groupBy, templates: selectedTemplates } = chartFilters;
  const { isRelative, relativeAmount, dateType } = relative;

  const [aggregations, setAggregations] = useState<TimeAggregationBucket[]>([]);
  const [schedulesById, setSchedulesById] = useState<Record<string, ScheduleDashboardSchedule>>({});
  const [loading, setLoading] = useState(false);
  const [mainError, setMainError] = useState(false);
  const [mainLoaded, setMainLoaded] = useState(false);
  const [showEventTypeDrawer, setShowEventTypeDrawer] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUsersState | null>(null);
  const [drawerState, setDrawerState] = useState<ScheduleDashboardDrawerState>({
    users: false,
  });

  const selectedDivs = useSelector(
    ({ divisions }: TasksDashboardReduxState) => divisions.selector.ids || [],
  );
  // Empty array = "all divisions of the org" for the dashboard API, and only for
  // org-wide roles; a division-scoped user keeps the explicit ids.
  const queryDivisionIds = useElasticDivisionIds();

  useSyncReportUrl(chartFilters, 'templates', selectedDivs);

  const orgId = useSelector(
    (state: TasksDashboardReduxState) => state.organizations.organization.id,
  );
  const assignedDivisions = useSelector(
    (state: CommonReduxState) =>
      state.divisions.assigned.data?.divisions ?? EMPTY_ASSIGNED_DIVISIONS,
  );
  const timeZone = useSelector(
    ({ user }: CommonReduxState) => user?.profile?.data?.timeZone,
  ) || DateUtil.getDefaultTimeZone();
  const q = useGetParams();
  const history = useHistory<{ shadow?: boolean }>();
  const { url } = useRouteMatch();
  const { location: { state: { shadow = false } = {} } = {} } = history || {};

  const divId = q.get('divId');
  const triggerId = q.get('triggerId')?.replace('/events', '');
  const dateKey = q.get('dateKey');
  const showEventTypes = !!dateKey?.includes('/event_types');
  const openTimelineEvents = !!divId && !!triggerId;

  const showEventTypesRef = useRef(showEventTypes);
  showEventTypesRef.current = showEventTypes;

  const selectedTemplatesRef = useRef(draftChartFilters.templates);

  const intlRef = useRef(intl);
  intlRef.current = intl;

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const initialFetchKeyRef = useRef<string | null>(null);
  const fetchGenerationRef = useRef(0);

  const fetchParamsRef = useRef({
    orgId,
    selectedDivs,
    queryDivisionIds,
    relative,
    dateRange,
    dateType,
    isRelative,
    relativeAmount,
    groupBy,
    selectedTemplates,
    timeZone,
  });

  fetchParamsRef.current = {
    orgId,
    selectedDivs,
    queryDivisionIds,
    relative,
    dateRange,
    dateType,
    isRelative,
    relativeAmount,
    groupBy,
    selectedTemplates,
    timeZone,
  };
  const chartFiltersRef = useRef(chartFilters);
  chartFiltersRef.current = chartFilters;

  const executeFetch = useCallback(
    (
      templatesOverride?: SelectedSchedulesState,
      { chartFiltersOverride }: { chartFiltersOverride?: TasksChartFilters } = {},
    ) => {
      const {
        orgId: currentOrgId,
        selectedDivs: currentSelectedDivs,
        queryDivisionIds: currentQueryDivisionIds,
        timeZone: currentTimeZone,
      } = fetchParamsRef.current;
      const chart = chartFiltersOverride ?? {
        relative: fetchParamsRef.current.relative,
        dateRange: fetchParamsRef.current.dateRange,
        groupBy: fetchParamsRef.current.groupBy,
        templates: templatesOverride ?? fetchParamsRef.current.selectedTemplates,
      };
      const templates = templatesOverride ?? chart.templates;
      const { groupBy: nextGroupBy } = chart;

      if (showEventTypesRef.current) {
        return;
      }

      if (!currentOrgId || !currentSelectedDivs.length) {
        return;
      }

      const generation = ++fetchGenerationRef.current;
      setLoading(true);
      setMainError(false);
      setMainLoaded(false);
      apiCallable(API_OPERATIONS.SCHEDULE_DASHBOARD)({
        organizationId: currentOrgId,
        divisionIds: currentQueryDivisionIds,
        uid: auth.currentUser?.uid,
        range: getTasksReportEpochRange(chart, currentTimeZone),
        calendarRange: getTasksReportCalendarRange(chart, currentTimeZone),
        interval: nextGroupBy,
        scheduleIds: [],
        templateIds: getTemplateIdsForApi(templates),
        type: SCHEDULE_TYPES.task,
      })
        .then(result => {
          if (generation !== fetchGenerationRef.current) return;

          const schedulesById = getDashboardSchedulesById(result);
          const { data } = camelize(result) as {
            data: {
              aggregations: { timeRange: { histogram: { buckets: TimeAggregationBucket[] } } };
            };
          };
          const buckets = data.aggregations.timeRange.histogram.buckets;
          setAggregations(buckets);
          setSchedulesById(schedulesById);
          setMainLoaded(true);
          setLoading(false);
        })
        .catch(error => {
          if (generation !== fetchGenerationRef.current) return;
          console.error('[TasksDashboard] API Error:', error);
          setAggregations([]);
          setSchedulesById({});
          setMainError(true);
          setMainLoaded(false);
          setLoading(false);
        });
    },
    [],
  );

  const historyRef = useRef(history);
  historyRef.current = history;

  const urlRef = useRef(url);
  urlRef.current = url;

  const toggleDrawer = useCallback((type: ScheduleDashboardDrawerType) => {
    setDrawerState(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const runQuery = useCallback(() => {
    executeFetch();
  }, [executeFetch]);

  const applyFilter = useCallback(
    (props: SetValueProps | SetValueProps[]) => {
      const propsList = restoreClearedReportFilters(
        props,
        TASKS_DASHBOARD_FILTER_FIELDS,
        () => buildInitTaskFilterValues(buildDefaultTaskChartFilters(), intlRef.current),
      );
      if (!propsList.length) return;

      const baseFilters = Object.keys(filtersRef.current).length
        ? filtersRef.current
        : initialFiltersRef.current;
      const nextFilters = propsList.reduce(
        (acc, current) => mergeFilterSetValueProps(acc, current),
        baseFilters,
      );
      selectedTemplatesRef.current = filterValuesToTaskFilters(nextFilters).templates;
      filtersRef.current = nextFilters;
      propsList.forEach(item => setFilters(item));
    },
    [setFilters],
  );

  const applyFilters = useCallback(() => {
    const baseFilters = Object.keys(filtersRef.current).length
      ? filtersRef.current
      : initialFiltersRef.current;
    const nextChartFilters = {
      ...filterValuesToTaskFilters(baseFilters),
      templates: selectedTemplatesRef.current,
    };
    setChartFilters(nextChartFilters);
    if (!showEventTypesRef.current) {
      executeFetch(undefined, { chartFiltersOverride: nextChartFilters });
    }
  }, [executeFetch]);

  const setSelectedTemplates = useCallback(
    (next: SelectedSchedulesState | ((prev: SelectedSchedulesState) => SelectedSchedulesState)) => {
      const currentSelection = selectedTemplatesRef.current;
      const resolved = typeof next === 'function' ? next(currentSelection) : next;

      if (areTemplateSelectionsEqual(currentSelection, resolved)) {
        return;
      }
      selectedTemplatesRef.current = resolved;

      const templatesProps = buildTaskFilterSetValueProps(
        TASKS_DASHBOARD_FILTER_FIELDS.TEMPLATES,
        resolved,
        intlRef.current,
      );

      filtersRef.current = mergeFilterSetValueProps(filtersRef.current, templatesProps);
      setFilters(templatesProps);
    },
    [setFilters],
  );

  const onOpenEventTypesDrawer = useCallback(
    ({ schedule = {}, dateKey: dateKeyArg, groupByArg }: OnOpenEventTypesDrawerParams) => {
      const {
        dateRange: currentDateRange,
        groupBy: currentGroupBy,
        relative: currentRelative,
        selectedTemplates: currentTemplates,
        timeZone: currentTimeZone,
      } = fetchParamsRef.current;

      const range = getTasksReportEpochRange({
        relative: currentRelative,
        dateRange: currentDateRange,
        groupBy: currentGroupBy,
        templates: currentTemplates,
      }, currentTimeZone);
      const path = getFullUrl(
        getRouteToTasksEventTypesDrawer(
          schedule.divId,
          range.gte,
          range.lte,
          currentGroupBy,
          schedule.id,
          dateKeyArg,
          groupByArg,
        ),
        urlRef.current,
      );
      historyRef.current.push(path, { shadow: true });
      setShowEventTypeDrawer(true);
    },
    [],
  );

  const goToTimeline = useCallback(
    ({
      divisionId,
      scheduleId,
      triggerID,
      dateKeyArg,
      groupByArg,
      finishedExecutions,
      expectedExecutions,
    }: GoToTimelineParams) => {
      const {
        orgId: currentOrgId,
        dateRange: currentDateRange,
        relative: currentRelative,
        groupBy: currentGroupBy,
        selectedTemplates: currentTemplates,
        timeZone: currentTimeZone,
      } = fetchParamsRef.current;

      const range = getTasksReportEpochRange({
        relative: currentRelative,
        dateRange: currentDateRange,
        groupBy: currentGroupBy,
        templates: currentTemplates,
      }, currentTimeZone);
      let path = getFullUrl(
        getRouteToTasksTimelineDrawer(
          currentOrgId,
          divisionId,
          triggerID,
          scheduleId,
          range.gte,
          range.lte,
          dateKeyArg,
          groupByArg,
        ),
        urlRef.current,
      );

      if (finishedExecutions != null && expectedExecutions != null) {
        const separator = path.includes('?') ? '&' : '?';
        path = `${path}${separator}finishedExecutions=${finishedExecutions}&expectedExecutions=${expectedExecutions}`;
      }

      historyRef.current.push(path, { shadow: true });
    },
    [],
  );

  const onBackFromTimeline = useCallback(() => {
    if (shadow) history.goBack();
    else history.push(getFullUrl(getRouteToDashboardTasks(), url));
  }, [shadow, history, url]);

  const dashboardDivisionIds = useMemo(
    () => getDashboardScheduleDivisionIds(schedulesById),
    [schedulesById],
  );
  const { getDivisionName } = useDivisionNames({
    organizationId: orgId,
    divisionIds: dashboardDivisionIds,
    assignedDivisions,
  });
  const schedulesByIdWithDivisionNames = useMemo(
    () => getDashboardSchedulesWithDivisionNames(schedulesById, getDivisionName),
    [schedulesById, getDivisionName],
  );

  const tableDataByDay = useMemo<TableDataByDay[]>(() => {
    return aggregations
      .slice()
      .reverse()
      .map(day => {
        const hasSchedule = !!day.bySchedule.buckets.length;
        if (!hasSchedule) return null;

        const dataSource = day.bySchedule.buckets
          .map(schAggregation => {
            const sch = schedulesByIdWithDivisionNames[schAggregation.key];
            if (!sch) return null;

            const scheduleData = calculateScheduleProgress(schAggregation);

            return {
              id: `${day.key}-${schAggregation.key}`,
              dateKey: day.key,
              schedule: sch,
              aggregation: schAggregation,
              scheduleData,
            };
          })
          .filter((row): row is NonNullable<typeof row> => row !== null);

        return {
          day: day.key,
          dataSource,
        };
      })
      .filter((dayData): dayData is TableDataByDay => dayData !== null);
  }, [aggregations, schedulesByIdWithDivisionNames]);

  useEffect(() => {
    set(TASKS_DASHBOARD_FILTERS_STORAGE_KEY, taskFiltersToStoragePayload(draftChartFilters));
  }, [draftChartFilters]);

  const selectedDivsKey = [...selectedDivs].sort().join(',');
  const queryDivisionIdsKey = queryDivisionIds.join(',');

  useEffect(() => {
    if (showEventTypesRef.current) {
      return;
    }

    if (!orgId || !selectedDivsKey) {
      return;
    }

    // `queryDivisionIds` can collapse to `[]` (org-wide "all divisions") after the
    // selector ids are already set. Key on both so that collapse refetches.
    const fetchKey = `${orgId}:${selectedDivsKey}:${queryDivisionIdsKey}`;
    if (initialFetchKeyRef.current === fetchKey) {
      return;
    }

    initialFetchKeyRef.current = fetchKey;
    // The panel is shared across screens and only seeds `filters` with ours in a mount effect, so
    // during this commit `filters` is still empty or holds the previous screen's values. Querying
    // the restored filters explicitly keeps the buckets aligned with the groupBy the panel is
    // about to show — otherwise a foreign `groupBy` labels, say, daily buckets as whole months.
    executeFetch(undefined, { chartFiltersOverride: chartFiltersRef.current });
  }, [executeFetch, initialChartFilters, orgId, queryDivisionIdsKey, selectedDivsKey]);

  const value = useMemo<TasksDashboardContextValue>(
    () => ({
      aggregations,
      chartFilters,
      draftChartFilters,
      hasPendingFilters,
      filters,
      initialFilters: initialFiltersRef.current,
      isRelative,
      relativeAmount,
      dateType,
      dateRange,
      selectedTemplates,
      schedulesById: schedulesByIdWithDivisionNames,
      loading,
      mainError,
      mainLoaded,
      showEventTypeDrawer,
      mainLoading: loading,
      groupBy,
      tableDataByDay,
      selectedUsers,
      drawerState,
      queryDivisionIds,
      orgId,
      timeZone,
      divId,
      triggerId,
      dateKey,
      showEventTypes,
      openTimelineEvents,
      disabledDate: disableCalendarDatesAfterToday,
      intl,
      url,
      setFilters: applyFilter,
      applyFilters,
      setSelectedTemplates,
      setShowEventTypeDrawer,
      setSelectedUsers,
      fetch: runQuery,
      onOpenEventTypesDrawer,
      goToTimeline,
      onBackFromTimeline,
      toggleDrawer,
      toggle,
      isOpen,
    }),
    [
      aggregations,
      chartFilters,
      draftChartFilters,
      hasPendingFilters,
      filters,
      isRelative,
      relativeAmount,
      dateType,
      dateRange,
      selectedTemplates,
      schedulesByIdWithDivisionNames,
      loading,
      mainError,
      mainLoaded,
      showEventTypeDrawer,
      groupBy,
      tableDataByDay,
      selectedUsers,
      drawerState,
      queryDivisionIds,
      orgId,
      timeZone,
      divId,
      triggerId,
      dateKey,
      showEventTypes,
      openTimelineEvents,
      intl,
      url,
      applyFilter,
      applyFilters,
      setSelectedTemplates,
      runQuery,
      onOpenEventTypesDrawer,
      goToTimeline,
      onBackFromTimeline,
      toggleDrawer,
      toggle,
      isOpen,
    ],
  );

  return <TasksDashboardContext.Provider value={value}>{children}</TasksDashboardContext.Provider>;
};

export const useTasksDashboardContext = (): TasksDashboardContextValue => {
  const context = useContext(TasksDashboardContext);
  if (!context) {
    throw new Error('useTasksDashboardContext must be used within a TasksDashboardProvider');
  }
  return context;
};
